import { useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import { isGlobalAdmin, managedGroups } from "../auth/roles";
import UserPicker from "../components/UserPicker";
import { Badge, Btn, Field, G_DARK, Input, Select, Sheet, SheetActions, SheetHeader, formatWhen } from "../components/Shared";

// ── Import from PPMS ─────────────────────────────────────────────────────────
//
// PLAN-2026-10-20-the-ppms-catalogue.md, item 6. The per-account *Sync with
// PPMS* left on 2026-10-20: it could only reach the projects PPMS assigned to
// one login, and the facility's projects mostly belong to nobody's login. The
// catalogue is every PPMS project, refreshed daily by the server, and this
// dialog finds one by name and creates it in a group the caller manages.
//
// The contract is `backend/portal/routers/ppms_catalog.py`. Three things it
// decides and this file only mirrors:
//   - two characters at least, or the search answers 422 — so a shorter term
//     asks nothing rather than showing the refusal on every first keystroke;
//   - a row already in the portal (`imported_as`) answers 409, so it is shown
//     as such and offers no Import: a button the server always refuses is a lie;
//     it names the owner, and links the project only when `imported_as.id` is
//     set — the server leaves it null for a project the caller may not open;
//   - the group must be one the caller manages (403), so the picker offers
//     exactly those — every group to an admin, who must ask the server for them.

const MIN_TERM = 2;
const DEBOUNCE_MS = 300;

// Who to ask about a project already in the portal, and a way in when the
// caller may open it (the maintainer, 2026-11-03). An operator reaches only
// the projects they own or are on, so "Already imported" alone would leave
// them with a project they can neither open nor ask about: the owner is named
// to everyone, and `imported_as.id` is null unless the caller may open it.
function ImportedAs({ imported }) {
  const owner = imported.owner_email
    ? (imported.owner_name && imported.owner_name !== imported.owner_email
        ? `${imported.owner_name}, ${imported.owner_email}` : imported.owner_email)
    : null;
  return (
    <div data-break style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
      {owner ? <>Owner: <a href={`mailto:${imported.owner_email}`} style={{ color: G_DARK }}>{owner}</a></> : "No owner on record"}
      {imported.id != null && (
        <> · <Link to={`/projects/${imported.id}`} style={{ color: G_DARK }}>Open {imported.name}</Link></>
      )}
    </div>
  );
}

function catalogPath(term, includeClosed) {
  const q = `/api/ppms/catalog?q=${encodeURIComponent(term)}`;
  return includeClosed ? `${q}&include_inactive=true` : q;
}

// `zone` is the page's display timezone, handed down rather than read here: the
// dialog then renders nothing of its own until the first search is asked.
export default function PpmsCatalogDialog({ onClose, onImported, zone }) {
  const id = useId();
  const { user } = useAuth();
  const isAdmin = isGlobalAdmin(user);

  const [term, setTerm] = useState("");
  const [includeClosed, setIncludeClosed] = useState(false);
  const [rows, setRows] = useState(null);       // null: nothing asked yet
  const [searching, setSearching] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  // The groups the caller manages carry their names on the grants; an admin
  // picks from every group, which only /api/admin/groups lists.
  const ownGroups = useMemo(() => managedGroups(user)
    .filter((g, i, all) => all.findIndex(o => o.id === g.id) === i), [user]);
  const [adminGroups, setAdminGroups] = useState([]);
  const groups = isAdmin ? adminGroups : ownGroups;
  // Somebody who manages one group has no choice to make; two or more must say
  // which, and nothing here guesses.
  const [groupId, setGroupId] = useState(() => (!isAdmin && ownGroups.length === 1 ? String(ownGroups[0].id) : ""));
  const [rep, setRep] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    api.get("/api/admin/groups").then(g => { if (alive && Array.isArray(g)) setAdminGroups(g); }).catch(() => {});
    return () => { alive = false; };
  }, [isAdmin]);

  // So an answer can say whether the catalogue is empty, stale or broken,
  // rather than leaving "no such project" as the only reading. Asked once, with
  // the first search: before one there is no answer for it to qualify.
  const [statusAsked, setStatusAsked] = useState(false);
  useEffect(() => {
    if (!statusAsked) return;
    let alive = true;
    api.get("/api/ppms/catalog/status").then(s => { if (alive) setStatus(s); }).catch(() => {});
    return () => { alive = false; };
  }, [statusAsked]);

  // Debounced: a request per keystroke would put six queries on the database
  // for *REMBAC*, five of them answered after the person had stopped reading.
  useEffect(() => {
    const needle = term.trim();
    if (needle.length < MIN_TERM) { setRows(null); return; }
    let alive = true;
    const timer = setTimeout(() => {
      setSearching(true);
      setStatusAsked(true);
      api.get(catalogPath(needle, includeClosed))
        .then(r => { if (alive) { setRows(Array.isArray(r) ? r : []); setError(null); } })
        .catch(err => { if (alive) setError(err.message); })
        .finally(() => { if (alive) setSearching(false); });
    }, DEBOUNCE_MS);
    return () => { alive = false; clearTimeout(timer); };
  }, [term, includeClosed]);

  const doImport = async row => {
    setBusyId(row.ppms_id);
    setError(null);
    try {
      const body = { group_id: Number(groupId) };
      if (rep) body.representative_id = rep.id;
      const project = await api.post(`/api/ppms/catalog/${row.ppms_id}/import`, body);
      onImported(project);
    } catch (err) {
      // Shown here, not on the page behind: the page is under the backdrop.
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const refreshed = status?.refreshed_at ? formatWhen(status.refreshed_at, { zone }) : null;

  return (
    <Sheet labelledBy={`${id}-t`} onClose={onClose} maxWidth={640}>
      <SheetHeader id={`${id}-t`} title="Import from PPMS" onClose={onClose} />
      <p style={{ margin: "0 0 14px", fontSize: 13, color: "#6b7280" }}>
        Find a PPMS project by name and create it here, in a group you manage. Its data goes to
        the portal's storage, in a folder named after the project, and stays there.
      </p>
      {status?.error && (
        <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.35)", fontSize: 13, color: "#92400e" }}>
          The last refresh failed: {status.error}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Field label="Search PPMS projects" hint="At least two characters of the project's name.">
          <Input type="search" value={term} onChange={e => setTerm(e.target.value)}
                 placeholder="e.g. REMBAC" autoFocus />
        </Field>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151" }}>
          <input type="checkbox" checked={includeClosed} onChange={e => setIncludeClosed(e.target.checked)} />
          Include projects closed in PPMS
        </label>
        <div data-form-grid style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Group" required hint="Where the project is created. Only the groups you manage are offered.">
            <Select value={groupId} onChange={e => setGroupId(e.target.value)}
                    options={groups.map(g => ({ value: String(g.id), label: g.name }))} />
          </Field>
          {/* Optional, as on a synced project: one without a representative
              is named one before it can be quoted. Never the caller — the
              server refuses that, as at every door. */}
          <Field label="Representative (optional)" hint="Can also be set on the project afterwards.">
            <UserPicker value={rep?.id ?? null} valueLabel={rep ? (rep.full_name || rep.email) : ""}
                        onChange={person => setRep(person || null)} />
          </Field>
        </div>
      </div>

      {error && (
        <div role="alert" style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}

      {searching && rows === null && (
        <p style={{ marginTop: 14, fontSize: 13, color: "#6b7280" }}>Searching…</p>
      )}
      {status && rows !== null && (
        <p style={{ marginTop: 14, marginBottom: 0, fontSize: 12, color: "#6b7280" }}>
          {refreshed
            ? <>The catalogue holds {status.count ?? "an unknown number of"} PPMS projects, refreshed {refreshed}.</>
            : <>The catalogue has not been refreshed yet, so nothing can be found in it.</>}
        </p>
      )}
      {rows !== null && rows.length === 0 && (
        <p style={{ marginTop: 14, fontSize: 13, color: "#6b7280" }}>
          No PPMS project{includeClosed ? "" : " open in PPMS"} matches “{term.trim()}”.
        </p>
      )}
      {rows !== null && rows.length > 0 && (
        <ul style={{ listStyle: "none", margin: "14px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map(row => {
            // Gone from PPMS cannot be imported (404); already here cannot (409).
            const importable = !row.imported_as && !row.gone;
            return (
              <li key={row.ppms_id} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 10 }}>
                <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                  <strong data-break style={{ fontSize: 14 }}>{row.name}</strong>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                    PPMS {row.ppms_id}{row.ppms_group ? ` · ${row.ppms_group}` : ""}{row.billing_code ? ` · ${row.billing_code}` : ""}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                    {row.imported_as && <Badge color="gray">Already imported</Badge>}
                    {!row.active && <Badge color="amber">Closed in PPMS</Badge>}
                    {row.gone && <Badge color="red">No longer in PPMS</Badge>}
                  </div>
                  {row.imported_as && <ImportedAs imported={row.imported_as} />}
                </div>
                {importable && (
                  <Btn onClick={() => doImport(row)} disabled={!groupId || busyId !== null}>
                    {busyId === row.ppms_id ? "Importing…" : "Import"}
                  </Btn>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <SheetActions>
        <Btn variant="secondary" onClick={onClose}>Close</Btn>
      </SheetActions>
    </Sheet>
  );
}
